<script setup lang="ts">
import Pagination from '~/components/design-system/pagination.vue';
import GenericError from '~/components/user-interface/generic-error.vue';
import GenericLoader from '~/components/user-interface/generic-loader.vue';
import { formatTransactionDate } from '~/utilities/data-transformers';
import { useServices } from '~/composables/useServices';
import Button from '~/components/design-system/button.vue';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';
import { FunnelIcon, TrashIcon } from '@heroicons/vue/24/solid';
import type { ITransactionRequest } from '~/interfaces/dto/wallet/request';
import TableV2 from '~/components/design-system/table-v2.vue';

interface ITableData {
  transactionId: number;
  date: string;
  gameName: string;
  transactionType: string;
  amount: number;
  isAssignBonus?: boolean;
}

const currentPage = ref<number>(0);
const totalPages = ref<number>(0);
const recordsPerPage = ref<number>(0);
const showingStart = ref<number>(0);
const showingEnd = ref<number>(0);
const totalResults = ref<number>(0);

const settings = useSiteStore();
const { $walletService } = useServices();
const { $t, $trackEvent, $dayjs } = useNuxtApp();
const { transactionsFilter, isTransactionsFilterEmpty } =
  useTransactionsFilter();

const activeLoader = ref<boolean>(true);
let currentTransactions;
const newData = reactive({ value: [] });
const errorMessage = ref('sorry-cant-find-results');
const showDetailTransaction = ref(false);
const transactionId = ref('');

const headers = ref([
  { id: 'transactionId', currency: false },
  { id: 'date', currency: false },
  { id: 'gameName', currency: false },
  { id: 'transactionType', currency: false },
  { id: 'amount', currency: true },
]);

const mobileHeaders = ref([
  { id: 'date', currency: false },
  { id: 'gameName', currency: false },
  { id: 'amount', currency: true },
]);

const readData = () => {
  currentPage.value = 1;
  recordsPerPage.value = 20;
  showingStart.value = 1;
  showingEnd.value = recordsPerPage.value;
};

const resetData = () => ({
  dateFrom: $dayjs().subtract(90, 'days').utc(true).toISOString(),
  pageIndex: 1,
  pageSize: 20,
});

const data = ref<ITransactionRequest>(resetData());

const getTransactions = async (pageIndex: number) => {
  newData.value = [];
  activeLoader.value = true;
  data.value.pageIndex = pageIndex;
  try {
    const response = await $walletService.fetchTransactions(data?.value);
    if (!response?.isSuccessful) {
      activeLoader.value = false;
      errorMessage.value = 'an-error-occurred';
      console.error(response.error);
      return;
    }
    const fetchedTransactions = response?.data;
    totalResults.value = fetchedTransactions.totalRecords;
    totalPages.value = fetchedTransactions.totalPages;
    currentPage.value = fetchedTransactions.pageIndex;
    activeLoader.value = false;
    fetchedTransactions.transactionLogs.forEach((record) => {
      const gameName = () => {
        if (record.transactionTypeId === '00000000-0000-0000-da7a-000000600141')
          return $t('jpc-assign-bonus');
        if (record.transactionTypeId === '00000000-0000-0000-da7a-000000600080')
          return $t('lucky-numbers');
        return record.note || '-';
      };
      const tableRow: ITableData = {
        transactionId: record.sequenceId,
        date: formatTransactionDate(record.createdDateTime),
        gameName: gameName(),
        transactionType: record.transactionTypeDescription || '-',
        amount: record.isCredit ? record.amount : -1 * record.amount,
        isAssignBonus:
          record.transactionTypeId === '00000000-0000-0000-da7a-000000600141',
      };
      newData.value.push(tableRow);
    });
    currentTransactions = fetchedTransactions.transactionLogs;
  } catch (err) {
    activeLoader.value = false;
    errorMessage.value = 'an-error-occurred';
    console.error(err);
  }
};

const next = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    getTransactions(currentPage.value);
    $trackEvent('Account', 'MY_ACCOUNT_HISTORY_TRANSACTION_PAGINATE');
  }
};

const select = (pageNo: number) => {
  currentPage.value = pageNo;
  getTransactions(currentPage.value);
  $trackEvent('Account', 'MY_ACCOUNT_HISTORY_TRANSACTION_PAGINATE');
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    getTransactions(currentPage.value);
    $trackEvent('Account', 'MY_ACCOUNT_HISTORY_TRANSACTION_PAGINATE');
  }
};

const updatePage = (start: number, end: number, total?: number) => {
  showingStart.value = start;
  showingEnd.value = end;
  totalPages.value = total ?? totalPages.value; //if total exists set totalPages to total
};

const showDetail = (value) => {
  transactionId.value = currentTransactions.find(
    (t) => t.sequenceId === value,
  ).financialReference;
  toggleShowDetail();
};

const toggleShowDetail = () => {
  showDetailTransaction.value = !showDetailTransaction.value;
};

onBeforeMount(async () => {
  transactionsFilter.value = null;
  readData();
  filterTransactions();
  $trackEvent('Account', 'MY_ACCOUNT_HISTORY');
});

onBeforeUnmount(() => (transactionsFilter.value = null));

const filterTransactions = () => {
  data.value = resetData();
  const transactionsRequest: ITransactionRequest = {
    ...data?.value,
    ...(transactionsFilter?.value as Partial<ITransactionRequest>),
    pageIndex: 1,
    pageSize: 20,
  };
  if ('showPayoutsOnly' in transactionsRequest)
    delete transactionsRequest['showPayoutsOnly'];
  Object.assign(data?.value, transactionsRequest);
  getTransactions(data?.value?.pageIndex);
  if (!isTransactionsFilterEmpty.value)
    $trackEvent('Account', 'MY_ACCOUNT_HISTORY_TRANSACTION_FILTER');
};

const refreshTransactions = async () => {
  await getTransactions(currentPage.value);
  $trackEvent('Account', 'MY_ACCOUNT_HISTORY_TRANSACTION_REFRESH');
};

const display = computed<boolean>(() => activeLoader.value);

watch(transactionsFilter, () => {
  if (!isTransactionsFilterEmpty.value) filterTransactions();
  else {
    data.value = resetData();
    readData();
    getTransactions(data?.value?.pageIndex);
    $trackEvent('Account', 'MY_ACCOUNT_HISTORY');
  }
});
</script>

<template>
  <UserLimitedFunctionalityHolster
    notice-key="transactions-summary"
    site-section="transactions"
  >
    <div v-if="!showDetailTransaction">
      <LazyUserAccountSettingsHeader
        v-if="newData.value.length"
        :title="$t('transaction-summary')"
        return-route="history"
        type="full-width"
      >
        <template #actions>
          <!--      TODO: uncomment this when it is time to add in export functionality-->
          <!--        <Button-->
          <!--          v-if="!display"-->
          <!--          element-name="transaction-summary-export-transaction"-->
          <!--          type="secondary"-->
          <!--          rounding="md"-->
          <!--          class="dark:!bg-dark-900 sm:dark:!bg-dark-700 gap-2 w-full justify-center"-->
          <!--          @click="do export"-->
          <!--        >-->
          <!--          <ArrowDownTrayIcon class="!w-5 !h-5" />-->
          <!--          <span class="capitalize">{{ $t('export') }}</span>-->
          <!--        </Button>-->
          <Button
            v-if="!display && !isTransactionsFilterEmpty"
            element-name="transaction-summary-reset-transactions-filter"
            type="secondary-alternative"
            rounding="md"
            class="gap-2 w-fit justify-center"
            @click="transactionsFilter = null"
          >
            <TrashIcon class="!w-5 !h-5" />
            <span
              class="capitalize truncate text-ellipsis hidden md:block font-normal text-sm"
            >
              {{ $t('reset') }}
            </span>
          </Button>

          <Button
            v-if="!display"
            element-name="transaction-summary-filter-transaction"
            :type="
              !isTransactionsFilterEmpty ? 'primary' : 'secondary-alternative'
            "
            rounding="md"
            class="gap-2 w-fit justify-center"
            @click="settings.activateModal('transactionsFilter')"
          >
            <FunnelIcon class="!w-5 !h-5" />
            <span
              class="capitalize truncate text-ellipsis hidden md:block font-normal text-sm"
            >
              {{ $t('filter') }}
            </span>
          </Button>

          <Button
            v-if="!display"
            element-name="transaction-summary-refresh-transaction"
            type="secondary-alternative"
            rounding="md"
            class="gap-2 w-fit justify-center"
            @click="refreshTransactions"
          >
            <ArrowPathIcon class="!w-5 !h-5" />
            <span
              class="capitalize truncate text-ellipsis hidden md:block font-normal text-sm"
            >
              {{ $t('refresh') }}
            </span>
          </Button>
        </template>
      </LazyUserAccountSettingsHeader>
      <GenericLoader v-if="display" :standalone="true" />
      <div v-else class="overflow-x-hidden">
        <!-- 🧹🧹 -->
        <TableV2
          v-if="newData.value.length"
          :original-data="newData.value"
          :headers="headers"
          :mobile-headers="mobileHeaders"
          :clickable="true"
          :total-pages="totalPages"
          :current-page="currentPage"
          :records-per-page="recordsPerPage"
          :total-results="totalResults"
          :search-on-header="`Game Name`"
          :mobile-b-p="`lg`"
          @update-page="updatePage"
          @row-click="showDetail"
        >
          <template #pagination>
            <div
              class="flex place-content-center justify-center pt-3 sticky bottom-0 rounded-b-lg"
            >
              <Pagination
                :current-page="currentPage"
                :total-pages="totalPages"
                :showing-start="showingStart"
                :showing-end="showingEnd"
                :total-results="totalResults"
                @next="next"
                @previous="previous"
                @select="select"
              />
            </div>
          </template>
        </TableV2>
        <div
          v-else
          class="w-full flex flex-col items-center justify-center gap-1 pb-4"
        >
          <GenericError state="danger" class="h-fit my-3">
            {{ $t(errorMessage) }}
          </GenericError>
          <Button
            v-if="!isTransactionsFilterEmpty"
            element-name="transaction-summary-reset-transactions-filter"
            type="tertiary"
            rounding="md"
            class="dark:!bg-dark-900 sm:dark:!bg-dark-700 gap-2 justify-center"
            @click="transactionsFilter = null"
          >
            <TrashIcon class="!w-5 !h-5" />
            <span class="capitalize">{{ $t('reset') }}</span>
          </Button>
        </div>
      </div>
    </div>
    <div v-if="showDetailTransaction">
      <AccountHistoryDetailedTransaction
        title="transaction-summary"
        :transaction-id="transactionId"
        @back="toggleShowDetail()"
      />
    </div>
  </UserLimitedFunctionalityHolster>
</template>
